/**
 * Map palettes on trial (Sam, 2026-11-01: "dusk by day, night after sunset", then "a day one for when it's bright
 * out?"). `auto` is the default: by the sun at the venue, day while it is up high, dusk for about the last hour before
 * sunset, night after. A phone can force one (Settings > Map look, operators only; stored on the phone), and dev
 * builds also take `?palette=day|dusk|night|auto|today`. `today` is the map as it was before the trial.
 *
 * It is a grade, not a second set of colours: greens become the palette's grass, the greys and purple-greys (walls,
 * paths, pavement, the desert) move toward lavender on a lightness curve, and strong colours (amenities, kinds, water)
 * are kept, so what a thing IS still reads the same way at night.
 */
const KEY = 'prii.map.palette';
export const PALETTE_NAMES = ['today', 'day', 'dusk', 'night', 'auto'];
/** The sun this high (degrees) or higher is day under auto; about an hour before sunset in spring at Indio. */
const DAY_ABOVE = 10;

const PALETTES = {
  // bright sun: lighter ground and paths so the map still reads with glare on the screen; costs more battery on an
  // OLED phone than dusk or night (more lit pixels at full brightness)
  day: {
    grass: [118, 0.3, 0.6], // #8cc68a-ish
    light: (l) => 0.22 + l * 0.78,
    sat: 0.12,
    strong: 1,
    fog: 0x6f6390, // the far edge still meets the app's dark background
    hemi: [0xf4eeff, 0x6a5e86, 1.6],
    sun: [0xfff4e8, 1.3],
  },
  dusk: {
    grass: [150, 0.26, 0.32], // #3c6652
    light: (l) => l * 0.85,
    sat: 0.15,
    strong: 1,
    fog: 0x1c1430,
    hemi: [0xe2d6ff, 0x3a2f52, 1.45],
    sun: [0xffe6d8, 1.1],
  },
  night: {
    grass: [254, 0.32, 0.19], // #2a2140
    light: (l) => l * 0.68,
    sat: 0.12,
    strong: 0.82,
    fog: 0x0d0918,
    hemi: [0xbfaeff, 0x241b38, 1.2],
    sun: [0xc8b8ff, 0.7], // moonlight
  },
};

/** What this phone asked for: 'auto' unless forced. */
export function palettePref() {
  if (typeof window === 'undefined') return 'auto';
  try {
    const q = import.meta.env.DEV && new URLSearchParams(window.location.search).get('palette');
    if (q && PALETTE_NAMES.includes(q)) setPalettePref(q);
    const v = window.localStorage.getItem(KEY);
    return PALETTE_NAMES.includes(v) ? v : 'auto';
  } catch {
    return 'auto';
  }
}
/** Force a palette on this phone ('auto' goes back to the sun). The next map opened is built in it. */
export function setPalettePref(name) {
  try {
    if (name === 'auto') window.localStorage.removeItem(KEY);
    else if (PALETTE_NAMES.includes(name)) window.localStorage.setItem(KEY, name);
  } catch {
    /* storage off: auto */
  }
}

/** The sun's height above the horizon in degrees at a place and time (low-precision almanac, about 0.1°). */
export function sunAltitude(lat, lon, when = new Date()) {
  const rad = Math.PI / 180;
  const d = when.getTime() / 86400000 - 10957.5; // days since 2000-01-01 12:00 UTC
  const g = (357.529 + 0.98560028 * d) * rad;
  const q = 280.459 + 0.98564736 * d;
  const L = (q + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * rad;
  const e = (23.439 - 0.00000036 * d) * rad;
  const ra = Math.atan2(Math.cos(e) * Math.sin(L), Math.cos(L));
  const dec = Math.asin(Math.sin(e) * Math.sin(L));
  const gmst = (18.697374558 + 24.06570982441908 * d) % 24;
  const ha = (gmst * 15 + lon) * rad - ra;
  return (
    Math.asin(
      Math.sin(lat * rad) * Math.sin(dec) + Math.cos(lat * rad) * Math.cos(dec) * Math.cos(ha),
    ) / rad
  );
}

/**
 * 'today' | 'day' | 'dusk' | 'night' for a map at lat/lon, now. Sunset is the sun's upper edge at the horizon
 * (-0.833°); the same edges run backwards at sunrise (night, then dusk, then day).
 */
export function paletteFor(lat, lon, when = new Date()) {
  const p = palettePref();
  if (p !== 'auto') return p;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return 'dusk';
  const alt = sunAltitude(lat, lon, when);
  return alt < -0.833 ? 'night' : alt < DAY_ABOVE ? 'dusk' : 'day';
}

let active = 'today';
/** Set by the scene as it starts building; everything below grades to it. */
export function setActivePalette(name) {
  active = PALETTES[name] ? name : 'today';
  return active;
}
export const activePalette = () => active;

function toHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const mx = Math.max(r, g, b),
    mn = Math.min(r, g, b),
    l = (mx + mn) / 2;
  if (mx === mn) return [0, 0, l];
  const d = mx - mn,
    s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
  const h =
    mx === r
      ? ((g - b) / d + (g < b ? 6 : 0)) * 60
      : mx === g
        ? ((b - r) / d + 2) * 60
        : ((r - g) / d + 4) * 60;
  return [h, s, l];
}
function fromHsl(h, s, l) {
  const k = (n) => (n + h / 30) % 12,
    a = s * Math.min(l, 1 - l);
  const f = (n) =>
    Math.round(255 * (l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))));
  return (f(0) << 16) | (f(8) << 8) | f(4);
}
const clamp = (v) => Math.max(0, Math.min(1, v));

/** A 0xRRGGBB colour in the active palette (unchanged under 'today'). */
export function gradeInt(hex) {
  const P = PALETTES[active];
  if (!P) return hex;
  const [h, s, l] = toHsl((hex >> 16) & 255, (hex >> 8) & 255, hex & 255);
  if (h >= 65 && h <= 175 && s > 0.12) {
    const [gh, gs, gl] = P.grass; // grass, trees, hedges: the palette's grass, keeping how much lighter or darker
    return fromHsl(gh, gs * Math.min(1.2, s / 0.3), clamp(gl * (l / 0.42)));
  }
  if (s > 0.5 && l > 0.18 && l < 0.88) return fromHsl(h, s, l * P.strong); // a colour that means something: kept
  return fromHsl(268, Math.min(0.42, s * 0.6 + P.sat), clamp(P.light(l)));
}
/** '#rrggbb' (or anything THREE.Color takes as a number/string) in the active palette. */
export function gradeHex(c) {
  if (active === 'today') return c;
  const n =
    typeof c === 'number'
      ? c
      : typeof c === 'string' && /^#[0-9a-f]{6}$/i.test(c)
        ? parseInt(c.slice(1), 16)
        : null;
  if (n == null) return c;
  return '#' + gradeInt(n).toString(16).padStart(6, '0');
}

/** Lights and fog for the active palette. */
export function gradeLights({ hemi, sun, fog }) {
  const P = PALETTES[active];
  if (!P) return;
  hemi.color.setHex(P.hemi[0]);
  hemi.groundColor.setHex(P.hemi[1]);
  hemi.intensity = P.hemi[2];
  sun.color.setHex(P.sun[0]);
  sun.intensity = P.sun[1];
  if (fog) fog.color.setHex(P.fog);
}

/**
 * Grades every material in the scene that has not been graded yet - the shared ones from mat() are graded when made,
 * the rest (hard-coded trees, fences, seats...) are caught here, before each render. Textured materials are skipped:
 * their colour multiplies a texture that was graded when it was painted, and labels are pictures that keep their colours.
 */
export function gradeScene(scene) {
  if (active === 'today') return;
  scene.traverse((o) => {
    const ms = o.material ? (Array.isArray(o.material) ? o.material : [o.material]) : null;
    if (!ms) return;
    for (const m of ms) {
      if (!m || m.userData.graded || o.isSprite) continue;
      m.userData.graded = true;
      if (m.map || m.vertexColors || !m.color) continue; // vertex colours were graded where they were painted
      m.color.setHex(gradeInt(m.color.getHex()));
    }
  });
}
